import { WorkerEntity } from 'syft-acp-core/store/types'
import { Dispatch, AnyAction } from 'redux'

// A booking as returned by GET /admin/workers/:worker_id/jobs/:job_id/bookings.
// Only the worker's currently-booked (active, upcoming, non-no-show) shifts for the
// job are returned; cancelled bookings are excluded server-side.
export type WorkerJobBooking = {
  shift_id: number
  start_time: number | string
  // Flex Compensation (FC): Flex pays the worker instead of billing the client.
  syft_compensation: boolean
  // Compensation Hours (CH): minimum-hours pay and billing may be created for the booking.
  syft_unpaid_hours_compensation: boolean
}

export type BookingCompensationDecision = {
  ch: boolean
  chDefaulted: boolean
  fc: boolean
  fcReason?: string
  fcApprover?: string
}

export type BookingCompensationDecisions = Record<number, BookingCompensationDecision>

export type FlexCompReasonResult = {
  confirmed: boolean
  syftCompReason?: string
  approver?: string
  otherReason?: string
}

export enum CompensationSaveStatus {
  Idle = 'idle',
  Saving = 'saving',
  Saved = 'saved',
  Error = 'error',
}

// One shift's overbook cover, as returned by GET /admin/shift_overbook_cover — purely
// informational (does not promote anyone). `worker_name` is only present when covered.
export type ShiftOverbookCover = {
  shift_id: number
  covered: boolean
  worker_id: number | null
  worker_name: string | null
}

// The dialog's two steps: pick shifts + reason, then the destructive spot decision.
export type CancelStep = 'select' | 'confirm'

// The cancellation reason as held in the dialog: an `endJobAssignmentReasons` id while
// picked from the dropdown, its label once resolved, or null before anything is chosen.
export type CancelReason = string | number | null

// Step 2's choice for what happens to the vacated spot. Both are a single
// /bookings/delete call (see buildCancelPayload's `deleteSpot`) — they must not be split
// into a delete-then-remove_spot pair, since flex-core only fires its overbook-promotion
// reaction on the reopen path, and shrinking the spot in a second call races against it
// (STSMPD-2072):
//   keep   — delete_spot: false. The spot stays on the shift and reopens to normal
//            offering; if a backup is first in line on the overbook, flex-core promotes
//            it automatically once the cancel lands. See ShiftOverbookCover, which only
//            decides how the option is described.
//   delete — delete_spot: true. flex-core shrinks/removes the spot instead of reopening
//            it, so no replacement is sought at all.
export type SpotDecision = 'keep' | 'delete'

export type BulkCancelModalOptions = {
  worker: Partial<WorkerEntity>
  jobID: number
  // The shift the Ops user clicked "Cancel" on; pre-selected once bookings load.
  currentShiftID: number
  // True when the job is a BLOCK BOOKING (job.bookable_individually === false) — shifts
  // are normally cancelled together. Drives a clarity banner in the dialog, and is also
  // what decides whether the dialog may present keeping the spot as an overbook fill:
  // flex-core only promotes an overbook backup onto a vacated spot for bookable-
  // individually jobs (MoveOverbookWorkerToParent#call: `return [] unless
  // job.bookable_individually`). Admins are exempt from the backend's must-book-whole-job
  // rule (see flex-core cancel_booking.rb), so individual-shift removal from a block IS
  // permitted; the banner just makes the block context clear. Absent/undefined => treat as
  // NOT a block booking.
  isBlockBooking?: boolean
}

export type BulkCancelBookingModalProps = {
  dispatch: Dispatch<AnyAction>
  item: {
    modalName: string
    isShown: boolean
    options: BulkCancelModalOptions
  }
}
